import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import {
  collection,
  query,
  where,
  getDocs,
  getCountFromServer,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { firestoreProd, firestoreDev } from "../../firebase";
import { getAuth } from "firebase/auth";
import {
  FaBuilding,
  FaSearch,
  FaDownload,
  FaSync,
  FaUserPlus,
  FaUserCheck,
  FaClipboardList,
  FaRoute,
  FaPuzzlePiece,
  FaUsers,
  FaChevronRight,
  FaInfoCircle,
  FaTrophy,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const tsToIso = (v) => {
  if (!v) return "";
  if (typeof v === "number") return new Date(v).toISOString();
  if (typeof v === "string") return v;
  if (v.seconds) return new Date(v.seconds * 1000).toISOString();
  if (v._seconds) return new Date(v._seconds * 1000).toISOString();
  if (typeof v.toDate === "function") return v.toDate().toISOString();
  return String(v);
};

const computeFlags = (user) => {
  if (!user) {
    return {
      hasUserDoc: false,
      extensionInstalled: false,
      hasQuestionnaire: false,
      finishedFirstTour: false,
      hasAnyFinishedOnboarding: false,
    };
  }
  const extensionInstalled =
    user.extensionInstalled === true || user.chromeExtensionInstalled === true;
  const hasQuestionnaire =
    !!user.questionnaire &&
    typeof user.questionnaire === "object" &&
    Object.keys(user.questionnaire).length > 0;
  const fo = Array.isArray(user.finishedOnboardings)
    ? user.finishedOnboardings
    : [];
  return {
    hasUserDoc: !!user.dateSignedUp || !!user.id,
    extensionInstalled,
    hasQuestionnaire,
    finishedFirstTour: fo.includes("first-tour"),
    hasAnyFinishedOnboarding: fo.length > 0,
  };
};

async function fetchUsersByEmail(db, emails) {
  const byEmail = new Map();
  const unique = [
    ...new Set(emails.filter(Boolean).map((e) => e.toLowerCase())),
  ];
  for (const group of chunk(unique, 10)) {
    const q = query(collection(db, "users"), where("email", "in", group));
    const snap = await getDocs(q);
    snap.forEach((d) => {
      const data = d.data();
      byEmail.set((data.email || "").toLowerCase(), { id: d.id, ...data });
    });
  }
  return byEmail;
}

const SUGGESTION_LIMIT = 12;

const orgDisplayName = (org) =>
  org.name || org.organizationName || org.companyName || org.id;

async function loadAllOrgs(db) {
  const snap = await getDocs(query(collection(db, "organizations")));
  return snap.docs
    .map((d) => {
      const data = d.data();
      return {
        id: d.id,
        name: data.name || data.organizationName || data.companyName || "",
        defaultForUserId:
          data.defaultForUserId ||
          data.adminUserId ||
          data.creatorUserId ||
          "",
        plan: data.subscription?.priceId || "",
      };
    })
    .sort((a, b) => orgDisplayName(a).localeCompare(orgDisplayName(b)));
}

// Extract "initials" from an org name, handling:
//   - word starts (after spaces/punctuation)
//   - camelCase transitions  (eMortgage → e, M)
//   - all-caps runs as acronyms (EMC → e, m, c)
// "eMortgage Capital" → "emc"
// "EMC Capital"       → "emcc"
// "a.shafei@smartcloudksa.com's Org" → "asscso" (no "emc")
function getInitials(name) {
  const out = [];
  for (let i = 0; i < name.length; i++) {
    const c = name[i];
    if (!/[A-Za-z0-9]/.test(c)) continue;
    const prev = i > 0 ? name[i - 1] : "";
    const next = i < name.length - 1 ? name[i + 1] : "";
    const isWordStart = i === 0 || !/[A-Za-z0-9]/.test(prev);
    const isCamelBoundary = /[a-z0-9]/.test(prev) && /[A-Z]/.test(c);
    const isAcronymChar =
      /[A-Z]/.test(c) && (/[A-Z]/.test(prev) || /[A-Z]/.test(next));
    if (isWordStart || isCamelBoundary || isAcronymChar) {
      out.push(c.toLowerCase());
    }
  }
  return out.join("");
}

function fuzzyMatchOrgs(orgs, term) {
  const raw = (term || "").trim().toLowerCase();
  if (!raw) return [];
  const normTerm = raw.replace(/[^a-z0-9]/g, "");
  if (!normTerm) return [];

  // Strict subsequence fallback — only for terms ≥ 4 chars, and only when the
  // match is reasonably compact (span ≤ 4× term length). Short terms like
  // "emc" can't trigger this, which avoids drowning the output in noise.
  const tightSubsequence = (name) => {
    if (normTerm.length < 4) return false;
    const normName = name.toLowerCase().replace(/[^a-z0-9]/g, "");
    let i = 0;
    let firstIdx = -1;
    let lastIdx = -1;
    for (let j = 0; j < normName.length && i < normTerm.length; j++) {
      if (normName[j] === normTerm[i]) {
        if (firstIdx === -1) firstIdx = j;
        lastIdx = j;
        i++;
      }
    }
    if (i !== normTerm.length) return false;
    const span = lastIdx - firstIdx + 1;
    return span <= normTerm.length * 4;
  };

  const tiered = [];
  for (const o of orgs) {
    const name = orgDisplayName(o);
    if (!name) continue;
    const lower = name.toLowerCase();
    const initials = getInitials(name);

    let tier = 0;
    if (lower === raw) tier = 5;
    else if (lower.startsWith(raw)) tier = 4;
    else if (initials.startsWith(normTerm)) tier = 3;
    else if (lower.includes(raw)) tier = 2;
    else if (initials.includes(normTerm)) tier = 2;
    else if (tightSubsequence(name)) tier = 1;

    if (tier > 0) tiered.push({ org: o, tier, name });
  }

  tiered.sort((a, b) => {
    if (b.tier !== a.tier) return b.tier - a.tier;
    return a.name.localeCompare(b.name);
  });

  if (typeof window !== "undefined" && window.__orgReportDebug) {
    // eslint-disable-next-line no-console
    console.log("[OrgReport] fuzzy", {
      term,
      cacheSize: orgs.length,
      tieredTop5: tiered.slice(0, 5).map((t) => ({
        id: t.org.id,
        name: t.name,
        initials: getInitials(t.name),
        tier: t.tier,
      })),
      sampleNames: orgs.slice(0, 5).map((o) => orgDisplayName(o)),
    });
  }

  return tiered.slice(0, SUGGESTION_LIMIT).map((t) => t.org);
}

async function findOrgIdsByEmail(db, email) {
  const q = query(
    collection(db, "organization-members"),
    where("email", "==", email.toLowerCase())
  );
  const snap = await getDocs(q);
  const ids = new Set();
  snap.forEach((d) => {
    const oid = d.data().organizationId;
    if (oid) ids.add(oid);
  });
  return [...ids];
}

/** Admin-only CRM / tracking docs (doc id = organization id). */
const ADMIN_ORG_TRACKING = "admin-org-tracking";

/** Plan bucket for active subscriptions (mirrors EnterpriseOrgs). */
function getPlan(subscription) {
  if (!subscription || subscription.status !== "active") return "FREE";
  const p = subscription.priceId || "";
  if (p.includes("enterprise")) return "ENTERPRISE";
  if (p.includes("growth")) return "GROWTH";
  if (p.includes("essentials")) return "ESSENTIALS";
  if (p.startsWith("price_")) return "GROWTH";
  return "FREE";
}

const PLAN_BADGE_STYLES = {
  FREE: { background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb" },
  ESSENTIALS: {
    background: "#f0fdfa",
    color: "#0f766e",
    border: "1px solid #99f6e4",
  },
  GROWTH: {
    background: "#eff6ff",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe",
  },
  ENTERPRISE: {
    background: "#f5f3ff",
    color: "#6d28d9",
    border: "1px solid #ddd6fe",
  },
};

function TopPlanBadge({ plan }) {
  const s = PLAN_BADGE_STYLES[plan] || PLAN_BADGE_STYLES.FREE;
  return (
    <span
      style={{
        ...s,
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.03em",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "currentColor",
          opacity: 0.7,
        }}
      />
      {plan}
    </span>
  );
}

/** Concurrent Firestore count queries (tune if you see throttling). */
const TOP_CLIENTS_CONCURRENCY = 28;

/**
 * Run async work over `items` with at most `limit` in-flight tasks.
 * Faster than tiny sequential batches while avoiding unbounded parallelism.
 */
async function mapPool(limit, items, fn) {
  if (!items.length) return [];
  const results = new Array(items.length);
  let next = 0;
  async function worker() {
    for (;;) {
      const i = next++;
      if (i >= items.length) return;
      results[i] = await fn(items[i], i);
    }
  }
  const n = Math.min(Math.max(1, limit), items.length);
  await Promise.all(Array.from({ length: n }, () => worker()));
  return results;
}

const ADDY_INTERNAL_DOMAINS = ["addy-ai.com", "addy.com", "heyaddy.com"];

function isAddyTeam(email) {
  if (!email) return false;
  const lower = email.toLowerCase();
  return ADDY_INTERNAL_DOMAINS.some((d) => lower.endsWith("@" + d));
}

/**
 * organization-members rows whose email is not an Addy internal domain.
 * Uses reads (not aggregate count) because Firestore cannot exclude by email pattern in count queries.
 */
async function countOrgMembersExcludingAddy(db, orgId) {
  const q = query(
    collection(db, "organization-members"),
    where("organizationId", "==", orgId)
  );
  const snap = await getDocs(q);
  let n = 0;
  snap.forEach((d) => {
    if (!isAddyTeam((d.data().email || "").trim())) n++;
  });
  return n;
}

async function loadPaidOrganizations(db) {
  const snap = await getDocs(collection(db, "organizations"));
  const out = [];
  snap.forEach((d) => {
    const data = d.data();
    const plan = getPlan(data.subscription);
    if (plan === "FREE") return;
    out.push({
      id: d.id,
      name: orgDisplayName({ id: d.id, ...data }),
      subscription: data.subscription || null,
      plan,
    });
  });
  return out;
}

/**
 * Top 20 only. Counts the sort metric for every paid org, then the other
 * metric only for the top 20 — roughly N+20 count queries instead of 2N.
 */
async function buildTopClientsRows(db, rankBy, onProgress) {
  onProgress?.("Loading organizations…");
  const paid = await loadPaidOrganizations(db);
  if (!paid.length) return [];

  if (rankBy === "loans") {
    onProgress?.(
      `Counting loans — ${paid.length} paid orgs (parallel, up to ${TOP_CLIENTS_CONCURRENCY} at once)…`
    );
    const withLoans = await mapPool(
      TOP_CLIENTS_CONCURRENCY,
      paid,
      async (o) => {
        const loansQ = query(collection(db, "organizations", o.id, "loans"));
        const loanCount = (await getCountFromServer(loansQ)).data().count;
        return { ...o, loanCount, memberCount: 0 };
      }
    );
    const top20 = [...withLoans]
      .sort((a, b) => b.loanCount - a.loanCount)
      .slice(0, 20);
    onProgress?.(
      "Top 20 by loans — counting client members (excl. Addy team)…"
    );
    await mapPool(TOP_CLIENTS_CONCURRENCY, top20, async (o) => {
      o.memberCount = await countOrgMembersExcludingAddy(db, o.id);
    });
    return top20;
  }

  onProgress?.(
    `Counting client members (excl. Addy) — ${paid.length} paid orgs (parallel, up to ${TOP_CLIENTS_CONCURRENCY} at once)…`
  );
  const withMembers = await mapPool(
    TOP_CLIENTS_CONCURRENCY,
    paid,
    async (o) => {
      const memberCount = await countOrgMembersExcludingAddy(db, o.id);
      return { ...o, memberCount, loanCount: 0 };
    }
  );
  const top20 = [...withMembers]
    .sort((a, b) => b.memberCount - a.memberCount)
    .slice(0, 20);
  onProgress?.("Top 20 by members — counting loans for those orgs only…");
  await mapPool(TOP_CLIENTS_CONCURRENCY, top20, async (o) => {
    const loansQ = query(collection(db, "organizations", o.id, "loans"));
    o.loanCount = (await getCountFromServer(loansQ)).data().count;
  });
  return top20;
}

function mergeTrackingDocs(orgRows, trackingSnapshots) {
  return orgRows.map((o, idx) => {
    const snap = trackingSnapshots[idx];
    const t = snap && snap.exists() ? snap.data() : {};
    return {
      ...o,
      tracking: {
        assignedDev: t.assignedDev || "",
        onboardingStatus: t.onboardingStatus || "",
        featuresInProgress: Array.isArray(t.featuresInProgress)
          ? t.featuresInProgress
          : [],
        nextSteps: t.nextSteps || "",
        improvements: t.improvements || "",
        notes: t.notes || "",
        lastUpdatedAt: t.lastUpdatedAt,
        lastUpdatedBy: t.lastUpdatedBy || "",
      },
    };
  });
}

function downloadTopClientsCsv(rows, rankBy) {
  const headers = [
    "rank",
    "orgId",
    "name",
    "plan",
    "clientMembers",
    "loans",
    "assignedDev",
    "onboardingStatus",
    "lastUpdatedBy",
    "lastUpdatedAt",
  ];
  const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
  const body = rows
    .map((r, i) =>
      [
        i + 1,
        r.id,
        r.name,
        r.plan,
        r.memberCount,
        r.loanCount,
        r.tracking.assignedDev,
        r.tracking.onboardingStatus,
        r.tracking.lastUpdatedBy,
        tsToIso(r.tracking.lastUpdatedAt),
      ]
        .map(esc)
        .join(",")
    )
    .join("\n");
  const csv = headers.join(",") + "\n" + body;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `top-clients-${rankBy}-prod.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function runReport(db, orgId) {
  const membersQ = query(
    collection(db, "organization-members"),
    where("organizationId", "==", orgId)
  );
  const membersSnap = await getDocs(membersQ);
  const members = membersSnap.docs.map((d) => ({ _docId: d.id, ...d.data() }));

  if (members.length === 0) return { rows: [] };

  const emails = members.map((m) => m.email).filter(Boolean);
  const usersByEmail = await fetchUsersByEmail(db, emails);

  const rows = members.map((m) => {
    const email = (m.email || "").toLowerCase();
    const user = usersByEmail.get(email) || null;
    const flags = computeFlags(user);
    return {
      email: m.email || "",
      role: m.role || "",
      status: m.status || "",
      dateSignedUp: tsToIso(user && user.dateSignedUp),
      extensionInstalledAt: tsToIso(user && user.extensionInstalledAt),
      extensionUninstalledAt: tsToIso(user && user.extensionUninstalledAt),
      finishedOnboardings:
        Array.isArray(user && user.finishedOnboardings) &&
        user.finishedOnboardings.length
          ? user.finishedOnboardings.join(", ")
          : "",
      ...flags,
    };
  });

  return { rows };
}

function downloadCsv(filename, rows) {
  const headers = [
    "email",
    "role",
    "status",
    "hasUserDoc",
    "hasQuestionnaire",
    "finishedFirstTour",
    "hasAnyFinishedOnboarding",
    "extensionInstalled",
    "dateSignedUp",
    "extensionInstalledAt",
    "extensionUninstalledAt",
    "finishedOnboardings",
  ];
  const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
  const body = rows
    .map((r) => headers.map((h) => esc(r[h])).join(","))
    .join("\n");
  const csv = headers.join(",") + "\n" + body;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const S = {
  page: {
    padding: "28px 32px 48px",
    minHeight: "100vh",
    background: "#f8f9fa",
    fontFamily: "inherit",
    width: "100%",
  },
  header: { marginBottom: 24 },
  h1: {
    fontSize: "1.5rem",
    fontWeight: 800,
    margin: "0 0 4px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "#111827",
  },
  h1Icon: {
    width: 34,
    height: 34,
    borderRadius: 9,
    background: "linear-gradient(135deg,#7f7fd5,#a78bfa)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.95rem",
    color: "#fff",
    flexShrink: 0,
  },
  subtitle: { color: "#6b7280", fontSize: "0.85rem", margin: 0 },
  tabsWrap: {
    display: "flex",
    gap: 4,
    marginBottom: 22,
    borderBottom: "2px solid #e5e7eb",
  },
  mainTab: (active) => ({
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "9px 20px",
    border: "none",
    borderRadius: "8px 8px 0 0",
    fontSize: "0.875rem",
    fontWeight: 600,
    cursor: "pointer",
    background: active ? "#fff" : "transparent",
    color: active ? "#7f7fd5" : "#6b7280",
    borderBottom: active ? "2px solid #7f7fd5" : "2px solid transparent",
    marginBottom: -2,
    transition: "all 0.15s",
  }),
  controls: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 14,
    marginBottom: 22,
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  fieldsRow: {
    display: "grid",
    gridTemplateColumns:
      "minmax(190px, 1fr) minmax(190px, 1.3fr) minmax(220px, 1.4fr) minmax(220px, 1.4fr)",
    gap: 12,
    width: "100%",
  },
  fieldCol: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: "0.68rem",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 700,
  },
  fieldInput: {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#111827",
    fontSize: "0.875rem",
    outline: "none",
    boxSizing: "border-box",
  },
  fieldHint: {
    fontSize: "0.75rem",
    color: "#9ca3af",
    marginTop: 10,
  },
  actionRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: 12,
  },
  btnPrimary: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 16px",
    borderRadius: 8,
    border: "none",
    background: "#7f7fd5",
    color: "#fff",
    fontSize: "0.85rem",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnGhost: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 16px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#374151",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  toggleWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 14px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
    color: "#374151",
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    userSelect: "none",
  },
  toggleInput: {
    margin: 0,
    cursor: "pointer",
    accentColor: "#7f7fd5",
    width: 14,
    height: 14,
  },
  toggleCount: {
    marginLeft: 6,
    color: "#9ca3af",
    fontWeight: 500,
  },
  // Picker card (multi-match)
  pickerCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "12px 14px 8px",
    marginBottom: 22,
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  pickerLabel: {
    fontSize: "0.7rem",
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    fontWeight: 700,
    marginBottom: 8,
    padding: "0 6px",
  },
  pickerRow: {
    padding: "10px 12px",
    borderRadius: 8,
    cursor: "pointer",
    transition: "background 0.1s",
  },
  pickerRowName: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#111827",
    marginBottom: 2,
  },
  pickerRowMeta: {
    fontSize: "0.72rem",
    color: "#9ca3af",
    fontFamily: "monospace",
  },
  // Stats
  statsRow: {
    display: "flex",
    gap: 14,
    marginBottom: 14,
    flexWrap: "wrap",
  },
  statCard: (color) => ({
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "14px 20px",
    minWidth: 140,
    flex: "1 1 140px",
    borderTop: `3px solid ${color}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  }),
  statLabel: {
    fontSize: "0.7rem",
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: 4,
    fontWeight: 600,
  },
  statValue: (color) => ({
    fontSize: "1.65rem",
    fontWeight: 800,
    color,
    lineHeight: 1,
  }),
  statSub: {
    fontSize: "0.7rem",
    color: "#9ca3af",
    marginTop: 4,
    fontWeight: 500,
  },
  // Funnel
  funnelWrap: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "16px 18px",
    marginBottom: 22,
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  funnelLabel: {
    fontSize: "0.7rem",
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    fontWeight: 700,
    marginBottom: 12,
  },
  funnelRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  funnelStep: {
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "8px 14px",
    minWidth: 90,
  },
  funnelStepLabel: {
    fontSize: "0.68rem",
    color: "#6b7280",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  funnelStepValue: {
    fontSize: "1.1rem",
    fontWeight: 800,
    color: "#111827",
    lineHeight: 1.2,
  },
  funnelArrow: { color: "#d1d5db", fontSize: "0.8rem" },
  // Table
  tableCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  tableHeader: {
    padding: "12px 18px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    background: "#fff",
  },
  tableTitle: { fontWeight: 700, color: "#111827", fontSize: "0.95rem" },
  filterChips: { display: "flex", gap: 6 },
  chip: (active) => ({
    padding: "6px 14px",
    borderRadius: 999,
    border: active ? "1px solid #7f7fd5" : "1px solid #e5e7eb",
    background: active ? "#7f7fd5" : "#fff",
    color: active ? "#fff" : "#374151",
    fontSize: "0.75rem",
    fontWeight: 600,
    cursor: "pointer",
    textTransform: "capitalize",
  }),
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" },
  thead: { background: "#f9fafb", borderBottom: "2px solid #e5e7eb" },
  th: {
    padding: "11px 14px",
    textAlign: "left",
    fontWeight: 700,
    color: "#374151",
    fontSize: "0.72rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  thInner: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
  },
  infoIconWrap: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    cursor: "help",
  },
  infoIcon: {
    color: "#9ca3af",
    fontSize: "0.78rem",
    transition: "color 0.15s",
  },
  infoBubble: {
    position: "fixed",
    transform: "translateX(-50%)",
    background: "#111827",
    color: "#f9fafb",
    padding: "8px 12px",
    borderRadius: 6,
    fontSize: "0.72rem",
    fontWeight: 500,
    letterSpacing: 0,
    textTransform: "none",
    lineHeight: 1.4,
    width: 240,
    whiteSpace: "normal",
    textAlign: "left",
    boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
    pointerEvents: "none",
    zIndex: 10000,
  },
  tr: { borderBottom: "1px solid #f3f4f6" },
  td: {
    padding: "12px 14px",
    verticalAlign: "middle",
    color: "#374151",
  },
  tdEmail: { fontWeight: 600, color: "#111827", whiteSpace: "nowrap" },
  tdMuted: { color: "#6b7280", whiteSpace: "nowrap", fontSize: "0.8rem" },
  tdMutedWrap: {
    color: "#6b7280",
    fontSize: "0.78rem",
    maxWidth: 260,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  empty: { textAlign: "center", padding: "60px 24px", color: "#9ca3af" },
};

// ─── Small display components ────────────────────────────────────────────────

const PILL_STYLES = {
  yes: { bg: "#ecfdf5", fg: "#047857", border: "#a7f3d0" },
  no: { bg: "#fef2f2", fg: "#b91c1c", border: "#fecaca" },
  pending: { bg: "#fffbeb", fg: "#92400e", border: "#fde68a" },
  accepted: { bg: "#eff6ff", fg: "#1d4ed8", border: "#bfdbfe" },
  neutral: { bg: "#f3f4f6", fg: "#6b7280", border: "#e5e7eb" },
};

function Pill({ tone, children }) {
  const s = PILL_STYLES[tone] || PILL_STYLES.neutral;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.03em",
        background: s.bg,
        color: s.fg,
        border: `1px solid ${s.border}`,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "currentColor",
          opacity: 0.7,
        }}
      />
      {children}
    </span>
  );
}

function BoolPill({ value }) {
  return <Pill tone={value ? "yes" : "no"}>{value ? "yes" : "no"}</Pill>;
}

function StatusPill({ status }) {
  if (status === "accepted") return <Pill tone="accepted">accepted</Pill>;
  if (status === "pending") return <Pill tone="pending">pending</Pill>;
  return <Pill tone="neutral">{status || "—"}</Pill>;
}

function InfoTip({ text }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);

  const show = () => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ left: r.left + r.width / 2, top: r.bottom + 8 });
  };
  const hide = () => setPos(null);

  return (
    <span
      ref={ref}
      onMouseEnter={show}
      onMouseLeave={hide}
      style={S.infoIconWrap}
    >
      <FaInfoCircle
        style={{ ...S.infoIcon, color: pos ? "#7f7fd5" : S.infoIcon.color }}
      />
      {pos
        ? ReactDOM.createPortal(
            <div
              style={{ ...S.infoBubble, left: pos.left, top: pos.top }}
            >
              {text}
            </div>,
            document.body
          )
        : null}
    </span>
  );
}

const ONBOARDING_STATUS_OPTIONS = [
  { value: "", label: "Not set" },
  { value: "in-progress", label: "In progress" },
  { value: "healthy", label: "Healthy" },
  { value: "at-risk", label: "At risk" },
  { value: "completed", label: "Completed" },
];

function topClientStatusTone(status) {
  if (status === "healthy") return "yes";
  if (status === "at-risk") return "no";
  if (status === "completed") return "accepted";
  if (status === "in-progress") return "pending";
  return "neutral";
}

const TC = {
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  notice: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 14,
    fontSize: "0.82rem",
    color: "#92400e",
  },
  chipRow: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  detailWrap: {
    background: "#fafafa",
    borderTop: "1px solid #e5e7eb",
    padding: "16px 18px",
    textAlign: "left",
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginTop: 12,
  },
  fieldBlock: { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontSize: "0.68rem",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 700,
  },
  input: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    fontSize: "0.85rem",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    minHeight: 72,
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    fontSize: "0.85rem",
    resize: "vertical",
    boxSizing: "border-box",
  },
  featureChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 999,
    background: "#eef2ff",
    border: "1px solid #c7d2fe",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#4338ca",
  },
};

function TopClientsTab({ onReady }) {
  const db = firestoreProd;
  const [loading, setLoading] = useState(false);
  const [progressMsg, setProgressMsg] = useState("");
  const [rows, setRows] = useState(null);
  const [rankBy, setRankBy] = useState("loans");
  const [expandedId, setExpandedId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRows, setDetailRows] = useState(null);
  const [excludeAddy, setExcludeAddy] = useState(true);
  const [crmForm, setCrmForm] = useState(null);
  const [featureInput, setFeatureInput] = useState("");
  const [saving, setSaving] = useState(false);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  useEffect(() => {
    onReady && onReady();
  }, [onReady]);

  const loadTop = useCallback(async () => {
    setLoading(true);
    setRows(null);
    setExpandedId(null);
    setDetailRows(null);
    setCrmForm(null);
    try {
      const top20 = await buildTopClientsRows(db, rankBy, setProgressMsg);
      setProgressMsg("Loading CRM tracking…");
      const trackingSnaps = await Promise.all(
        top20.map((o) => getDoc(doc(db, ADMIN_ORG_TRACKING, o.id)))
      );
      const merged = mergeTrackingDocs(top20, trackingSnaps);
      setRows(merged);
      toast.success(
        `Loaded top ${merged.length} clients by ${
          rankBy === "loans" ? "loans" : "client members (excl. Addy)"
        }`
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error(e.message || "Failed to load top clients");
    } finally {
      setLoading(false);
      setProgressMsg("");
    }
  }, [db, rankBy]);

  useEffect(() => {
    loadTop();
  }, [loadTop]);

  useEffect(() => {
    if (!expandedId) {
      setDetailRows(null);
      setCrmForm(null);
      setFeatureInput("");
      return;
    }
    const row = rowsRef.current?.find((r) => r.id === expandedId);
    if (!row) return;
    setCrmForm({
      assignedDev: row.tracking.assignedDev || "",
      onboardingStatus: row.tracking.onboardingStatus || "",
      featuresInProgress: [...(row.tracking.featuresInProgress || [])],
      nextSteps: row.tracking.nextSteps || "",
      improvements: row.tracking.improvements || "",
      notes: row.tracking.notes || "",
    });
    setDetailLoading(true);
    setDetailRows(null);
    let cancelled = false;
    (async () => {
      try {
        const { rows: r } = await runReport(db, expandedId);
        if (!cancelled) setDetailRows(r);
      } catch (e) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error(e);
          toast.error(e.message || "Failed to load org members");
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [expandedId, db]);

  const detailScoped = useMemo(() => {
    if (!detailRows) return null;
    const scoped = excludeAddy
      ? detailRows.filter((r) => !isAddyTeam(r.email))
      : detailRows;
    const total = scoped.length;
    const pending = scoped.filter((r) => r.status === "pending").length;
    const accepted = scoped.filter((r) => r.status === "accepted").length;
    const hasUserDoc = scoped.filter((r) => r.hasUserDoc).length;
    const ext = scoped.filter((r) => r.extensionInstalled).length;
    const quest = scoped.filter((r) => r.hasQuestionnaire).length;
    const tour = scoped.filter((r) => r.finishedFirstTour).length;
    const anyOB = scoped.filter((r) => r.hasAnyFinishedOnboarding).length;
    return {
      scopedRows: scoped,
      stats: { total, pending, accepted, hasUserDoc, ext, quest, tour, anyOB },
    };
  }, [detailRows, excludeAddy]);

  const addyTeamCountDetail = useMemo(
    () =>
      detailRows ? detailRows.filter((r) => isAddyTeam(r.email)).length : 0,
    [detailRows]
  );

  const handleSaveCrm = async () => {
    if (!expandedId || !crmForm) return;
    setSaving(true);
    try {
      const auth = getAuth();
      const by =
        auth.currentUser?.email || auth.currentUser?.uid || "unknown";
      await setDoc(
        doc(db, ADMIN_ORG_TRACKING, expandedId),
        {
          organizationId: expandedId,
          assignedDev: (crmForm.assignedDev || "").trim(),
          onboardingStatus: crmForm.onboardingStatus || "",
          featuresInProgress: (crmForm.featuresInProgress || []).filter(
            Boolean
          ),
          nextSteps: crmForm.nextSteps || "",
          improvements: crmForm.improvements || "",
          notes: crmForm.notes || "",
          lastUpdatedAt: serverTimestamp(),
          lastUpdatedBy: by,
        },
        { merge: true }
      );
      const now = new Date();
      setRows((prev) =>
        prev
          ? prev.map((r) =>
              r.id === expandedId
                ? {
                    ...r,
                    tracking: {
                      ...r.tracking,
                      assignedDev: (crmForm.assignedDev || "").trim(),
                      onboardingStatus: crmForm.onboardingStatus || "",
                      featuresInProgress: [
                        ...(crmForm.featuresInProgress || []),
                      ].filter(Boolean),
                      nextSteps: crmForm.nextSteps || "",
                      improvements: crmForm.improvements || "",
                      notes: crmForm.notes || "",
                      lastUpdatedBy: by,
                      lastUpdatedAt: now,
                    },
                  }
                : r
            )
          : prev
      );
      toast.success("Saved to admin-org-tracking");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    const v = featureInput.trim();
    if (!v || !crmForm) return;
    setCrmForm({
      ...crmForm,
      featuresInProgress: [...crmForm.featuresInProgress, v],
    });
    setFeatureInput("");
  };

  const removeFeature = (idx) => {
    if (!crmForm) return;
    setCrmForm({
      ...crmForm,
      featuresInProgress: crmForm.featuresInProgress.filter(
        (_, i) => i !== idx
      ),
    });
  };

  const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);

  return (
    <div>
      <p style={{ ...S.subtitle, marginBottom: 14 }}>
        Production only · Top 20 paid orgs (Enterprise / Growth / Essentials) by
        loans or <b>client members</b> (excludes @addy-ai.com / @heyaddy.com).
        CRM fields live in{" "}
        <code style={{ fontSize: "0.8em" }}>{ADMIN_ORG_TRACKING}</code>.
      </p>
      <div style={TC.notice}>
        Member totals exclude Addy internal emails. We count the sort metric for
        every paid org in parallel, then only fill the other metric for the top
        20. Client member counts use member document reads (not aggregate
        queries). The full organizations list still loads once from Firestore.
      </div>
      <div style={TC.toolbar}>
        <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#374151" }}>
          Rank by:
        </span>
        {["loans", "members"].map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setRankBy(k)}
            style={S.chip(rankBy === k)}
          >
            {k === "loans" ? "Loans" : "Client members"}
          </button>
        ))}
        <button
          type="button"
          onClick={loadTop}
          disabled={loading}
          style={{
            ...S.btnPrimary,
            opacity: loading ? 0.65 : 1,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? <FaSync className="obp-spin" /> : <FaSync />}{" "}
          Refresh
        </button>
        {rows && rows.length ? (
          <button
            type="button"
            onClick={() => downloadTopClientsCsv(rows, rankBy)}
            style={S.btnGhost}
          >
            <FaDownload /> CSV
          </button>
        ) : null}
      </div>
      {loading ? (
        <div style={S.empty}>
          <FaSync className="obp-spin" style={{ fontSize: "1.5rem" }} />
          <div style={{ marginTop: 12 }}>{progressMsg || "Loading…"}</div>
        </div>
      ) : rows && rows.length ? (
        <div style={S.tableCard}>
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead style={S.thead}>
                <tr>
                  <th style={S.th} />
                  <th style={S.th}>#</th>
                  <th style={S.th}>Organization</th>
                  <th style={S.th}>Plan</th>
                  <th
                    style={S.th}
                    title="Excludes @addy-ai.com and @heyaddy.com organization-members"
                  >
                    Client members
                  </th>
                  <th style={S.th}>Loans</th>
                  <th style={S.th}>Assigned dev</th>
                  <th style={S.th}>Onboarding</th>
                  <th style={S.th}>Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <React.Fragment key={row.id}>
                    <tr
                      style={{
                        ...S.tr,
                        cursor: "pointer",
                        background:
                          expandedId === row.id ? "#f5f3ff" : undefined,
                      }}
                      onClick={() =>
                        setExpandedId((id) =>
                          id === row.id ? null : row.id
                        )
                      }
                    >
                      <td style={S.td}>
                        {expandedId === row.id ? (
                          <FaChevronUp color="#7f7fd5" />
                        ) : (
                          <FaChevronDown color="#9ca3af" />
                        )}
                      </td>
                      <td style={S.td}>{idx + 1}</td>
                      <td style={{ ...S.td, ...S.tdEmail }}>
                        {row.name || row.id}
                        <div
                          style={{
                            fontSize: "0.72rem",
                            color: "#9ca3af",
                            fontFamily: "monospace",
                            fontWeight: 400,
                          }}
                        >
                          {row.id}
                        </div>
                      </td>
                      <td style={S.td}>
                        <TopPlanBadge plan={row.plan} />
                      </td>
                      <td style={S.td}>{row.memberCount}</td>
                      <td style={S.td}>{row.loanCount}</td>
                      <td style={{ ...S.td, ...S.tdMutedWrap }}>
                        {row.tracking.assignedDev || "—"}
                      </td>
                      <td style={S.td}>
                        {row.tracking.onboardingStatus ? (
                          <Pill tone={topClientStatusTone(row.tracking.onboardingStatus)}>
                            {row.tracking.onboardingStatus}
                          </Pill>
                        ) : (
                          <span style={{ color: "#9ca3af" }}>—</span>
                        )}
                      </td>
                      <td style={{ ...S.td, ...S.tdMuted }}>
                        {row.tracking.lastUpdatedAt
                          ? tsToIso(row.tracking.lastUpdatedAt).slice(0, 10)
                          : "—"}
                      </td>
                    </tr>
                    {expandedId === row.id ? (
                      <tr style={S.tr}>
                        <td colSpan={9} style={{ padding: 0 }}>
                          <div
                            style={TC.detailWrap}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              style={{
                                fontWeight: 700,
                                marginBottom: 8,
                                color: "#111827",
                              }}
                            >
                              Overview · {row.name}
                            </div>
                            {detailLoading ? (
                              <div style={{ color: "#6b7280" }}>
                                <FaSync className="obp-spin" /> Loading member
                                onboarding…
                              </div>
                            ) : detailScoped ? (
                              <>
                                <label style={{ ...S.toggleWrap, marginBottom: 12 }}>
                                  <input
                                    type="checkbox"
                                    checked={excludeAddy}
                                    onChange={(e) =>
                                      setExcludeAddy(e.target.checked)
                                    }
                                    style={S.toggleInput}
                                  />
                                  <span>
                                    Exclude Addy team
                                    {addyTeamCountDetail > 0 ? (
                                      <span style={S.toggleCount}>
                                        ({addyTeamCountDetail})
                                      </span>
                                    ) : null}
                                  </span>
                                </label>
                                <div style={S.statsRow}>
                                  <div style={S.statCard("#7f7fd5")}>
                                    <div style={S.statLabel}>Total members</div>
                                    <div style={S.statValue("#7f7fd5")}>
                                      {detailScoped.stats.total}
                                    </div>
                                  </div>
                                  <div style={S.statCard("#f59e0b")}>
                                    <div style={S.statLabel}>Pending</div>
                                    <div style={S.statValue("#f59e0b")}>
                                      {detailScoped.stats.pending}
                                    </div>
                                  </div>
                                  <div style={S.statCard("#10b981")}>
                                    <div style={S.statLabel}>Accepted</div>
                                    <div style={S.statValue("#10b981")}>
                                      {detailScoped.stats.accepted}
                                    </div>
                                  </div>
                                  <div style={S.statCard("#3b82f6")}>
                                    <div style={S.statLabel}>Extension</div>
                                    <div style={S.statValue("#3b82f6")}>
                                      {detailScoped.stats.ext}
                                    </div>
                                    <div style={S.statSub}>
                                      {pct(
                                        detailScoped.stats.ext,
                                        detailScoped.stats.total
                                      )}
                                      %
                                    </div>
                                  </div>
                                </div>
                                <div style={S.statsRow}>
                                  <div style={S.statCard("#8b5cf6")}>
                                    <div style={S.statLabel}>User doc</div>
                                    <div style={S.statValue("#8b5cf6")}>
                                      {detailScoped.stats.hasUserDoc}
                                    </div>
                                  </div>
                                  <div style={S.statCard("#ec4899")}>
                                    <div style={S.statLabel}>Questionnaire</div>
                                    <div style={S.statValue("#ec4899")}>
                                      {detailScoped.stats.quest}
                                    </div>
                                  </div>
                                  <div style={S.statCard("#06b6d4")}>
                                    <div style={S.statLabel}>First tour</div>
                                    <div style={S.statValue("#06b6d4")}>
                                      {detailScoped.stats.tour}
                                    </div>
                                  </div>
                                  <div style={S.statCard("#14b8a6")}>
                                    <div style={S.statLabel}>Any onboarding</div>
                                    <div style={S.statValue("#14b8a6")}>
                                      {detailScoped.stats.anyOB}
                                    </div>
                                  </div>
                                </div>
                                <div style={S.funnelWrap}>
                                  <div style={S.funnelLabel}>Adoption funnel</div>
                                  <div style={S.funnelRow}>
                                    {[
                                      {
                                        label: "invited",
                                        value: detailScoped.stats.total,
                                      },
                                      {
                                        label: "accepted",
                                        value: detailScoped.stats.accepted,
                                      },
                                      {
                                        label: "user doc",
                                        value: detailScoped.stats.hasUserDoc,
                                      },
                                      {
                                        label: "questionnaire",
                                        value: detailScoped.stats.quest,
                                      },
                                      {
                                        label: "first-tour",
                                        value: detailScoped.stats.tour,
                                      },
                                      {
                                        label: "extension",
                                        value: detailScoped.stats.ext,
                                      },
                                    ].map((st, i, arr) => (
                                      <React.Fragment key={st.label}>
                                        <div style={S.funnelStep}>
                                          <div style={S.funnelStepLabel}>
                                            {st.label}
                                          </div>
                                          <div style={S.funnelStepValue}>
                                            {st.value}
                                          </div>
                                        </div>
                                        {i < arr.length - 1 ? (
                                          <FaChevronRight style={S.funnelArrow} />
                                        ) : null}
                                      </React.Fragment>
                                    ))}
                                  </div>
                                </div>
                                <div style={S.tableCard}>
                                  <div style={S.tableHeader}>
                                    <div style={S.tableTitle}>
                                      Members (
                                      {detailScoped.scopedRows.length})
                                    </div>
                                  </div>
                                  <div style={{ overflowX: "auto" }}>
                                    <table style={S.table}>
                                      <thead style={S.thead}>
                                        <tr>
                                          {COLUMNS.map((c) => (
                                            <th key={c.label} style={S.th}>
                                              <span style={S.thInner}>
                                                {c.label}
                                                <InfoTip text={c.tip} />
                                              </span>
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {detailScoped.scopedRows.map(
                                          (r, j) => (
                                            <tr key={r.email + j} style={S.tr}>
                                              <td
                                                style={{ ...S.td, ...S.tdEmail }}
                                              >
                                                {r.email}
                                              </td>
                                              <td style={S.td}>{r.role}</td>
                                              <td style={S.td}>
                                                <StatusPill status={r.status} />
                                              </td>
                                              <td style={S.td}>
                                                <BoolPill value={r.hasUserDoc} />
                                              </td>
                                              <td style={S.td}>
                                                <BoolPill
                                                  value={r.hasQuestionnaire}
                                                />
                                              </td>
                                              <td style={S.td}>
                                                <BoolPill
                                                  value={r.finishedFirstTour}
                                                />
                                              </td>
                                              <td style={S.td}>
                                                <BoolPill
                                                  value={
                                                    r.hasAnyFinishedOnboarding
                                                  }
                                                />
                                              </td>
                                              <td style={S.td}>
                                                <BoolPill
                                                  value={r.extensionInstalled}
                                                />
                                              </td>
                                              <td
                                                style={{ ...S.td, ...S.tdMuted }}
                                              >
                                                {r.dateSignedUp
                                                  ? r.dateSignedUp.slice(0, 10)
                                                  : "—"}
                                              </td>
                                              <td
                                                style={{ ...S.td, ...S.tdMuted }}
                                              >
                                                {r.extensionInstalledAt
                                                  ? r.extensionInstalledAt.slice(
                                                      0,
                                                      10
                                                    )
                                                  : "—"}
                                              </td>
                                              <td
                                                style={{
                                                  ...S.td,
                                                  ...S.tdMutedWrap,
                                                }}
                                                title={r.finishedOnboardings}
                                              >
                                                {r.finishedOnboardings || "—"}
                                              </td>
                                            </tr>
                                          )
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </>
                            ) : null}

                            {crmForm ? (
                              <>
                                <div
                                  style={{
                                    fontWeight: 700,
                                    marginTop: 20,
                                    marginBottom: 10,
                                    color: "#111827",
                                  }}
                                >
                                  Client tracking (CRM)
                                </div>
                                <div style={TC.detailGrid}>
                                  <div style={TC.fieldBlock}>
                                    <div style={TC.label}>Assigned developer</div>
                                    <input
                                      style={TC.input}
                                      value={crmForm.assignedDev}
                                      onChange={(e) =>
                                        setCrmForm({
                                          ...crmForm,
                                          assignedDev: e.target.value,
                                        })
                                      }
                                      placeholder="name@company.com"
                                    />
                                  </div>
                                  <div style={TC.fieldBlock}>
                                    <div style={TC.label}>Onboarding status</div>
                                    <select
                                      style={TC.input}
                                      value={crmForm.onboardingStatus}
                                      onChange={(e) =>
                                        setCrmForm({
                                          ...crmForm,
                                          onboardingStatus: e.target.value,
                                        })
                                      }
                                    >
                                      {ONBOARDING_STATUS_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                          {o.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                                <div style={{ ...TC.fieldBlock, marginTop: 12 }}>
                                  <div style={TC.label}>Features in progress</div>
                                  <div style={TC.chipRow}>
                                    <input
                                      style={{ ...TC.input, maxWidth: 280 }}
                                      value={featureInput}
                                      onChange={(e) =>
                                        setFeatureInput(e.target.value)
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.preventDefault();
                                          addFeature();
                                        }
                                      }}
                                      placeholder="Add a feature…"
                                    />
                                    <button
                                      type="button"
                                      onClick={addFeature}
                                      style={S.btnGhost}
                                    >
                                      Add
                                    </button>
                                  </div>
                                  <div style={{ ...TC.chipRow, marginTop: 8 }}>
                                    {crmForm.featuresInProgress.map((f, fi) => (
                                      <span key={fi + f} style={TC.featureChip}>
                                        {f}
                                        <button
                                          type="button"
                                          aria-label="Remove"
                                          onClick={() => removeFeature(fi)}
                                          style={{
                                            border: "none",
                                            background: "none",
                                            cursor: "pointer",
                                            padding: 0,
                                            color: "#6366f1",
                                            fontWeight: 800,
                                          }}
                                        >
                                          ×
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <div style={TC.detailGrid}>
                                  <div style={TC.fieldBlock}>
                                    <div style={TC.label}>Next steps</div>
                                    <textarea
                                      style={TC.textarea}
                                      value={crmForm.nextSteps}
                                      onChange={(e) =>
                                        setCrmForm({
                                          ...crmForm,
                                          nextSteps: e.target.value,
                                        })
                                      }
                                    />
                                  </div>
                                  <div style={TC.fieldBlock}>
                                    <div style={TC.label}>Improvements</div>
                                    <textarea
                                      style={TC.textarea}
                                      value={crmForm.improvements}
                                      onChange={(e) =>
                                        setCrmForm({
                                          ...crmForm,
                                          improvements: e.target.value,
                                        })
                                      }
                                    />
                                  </div>
                                </div>
                                <div style={{ ...TC.fieldBlock, marginTop: 12 }}>
                                  <div style={TC.label}>Notes</div>
                                  <textarea
                                    style={{ ...TC.textarea, minHeight: 88 }}
                                    value={crmForm.notes}
                                    onChange={(e) =>
                                      setCrmForm({
                                        ...crmForm,
                                        notes: e.target.value,
                                      })
                                    }
                                  />
                                </div>
                                <div style={{ ...S.actionRow, marginTop: 16 }}>
                                  <button
                                    type="button"
                                    onClick={handleSaveCrm}
                                    disabled={saving}
                                    style={{
                                      ...S.btnPrimary,
                                      opacity: saving ? 0.65 : 1,
                                    }}
                                  >
                                    {saving ? (
                                      <FaSync className="obp-spin" />
                                    ) : null}{" "}
                                    Save tracking
                                  </button>
                                </div>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div style={S.empty}>No paid organizations found.</div>
      )}
    </div>
  );
}

const COLUMNS = [
  { label: "Email", tip: "User's email from the organization-members record." },
  {
    label: "Role",
    tip: "Role assigned in this org (Admin, Loan Officer, Processor, etc.). Set when the invite is issued.",
  },
  {
    label: "Status",
    tip: 'Invite status. "pending" = invite sent but not yet accepted. "accepted" = user joined this org.',
  },
  {
    label: "User doc",
    tip: "Whether a users/{uid} document exists in Firestore — i.e. they signed up to Addy at all (could be from this invite or independently).",
  },
  {
    label: "Quest.",
    tip: "Whether they completed the signup questionnaire (company name, website, LOS, phone, how-they-heard). Set via POST /onboarding-questionnaire.",
  },
  {
    label: "Tour",
    tip: 'Whether they finished the in-app first-tour. Set when "first-tour" is added to the user\'s finishedOnboardings array.',
  },
  {
    label: "Any OB",
    tip: 'Whether they have ANY entry in finishedOnboardings (first-tour, documents, people, test-loan-intro, review-document, no-assistants, etc.). Looser engagement signal.',
  },
  {
    label: "Ext.",
    tip: "Chrome extension installed. True if either extensionInstalled or chromeExtensionInstalled is true on the user doc.",
  },
  {
    label: "Signed up",
    tip: "Date the user document was created (dateSignedUp field). Empty for invites that haven't been accepted yet.",
  },
  {
    label: "Ext. installed",
    tip: "Date the extension was first installed (extensionInstalledAt). Empty if never installed.",
  },
  {
    label: "Finished onboardings",
    tip: "Full list of onboarding flows the user has completed. Used to drive the Tour and Any OB columns.",
  },
];

// ─── Page ────────────────────────────────────────────────────────────────────

const OrgUsersReportPage = ({ onReady }) => {
  const [activeTab, setActiveTab] = useState("report");
  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [orgIdInput, setOrgIdInput] = useState("");
  const [env, setEnv] = useState("prod");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [excludeAddy, setExcludeAddy] = useState(true);
  const [searchedOrgId, setSearchedOrgId] = useState("");
  const [searchedOrgName, setSearchedOrgName] = useState("");
  const [pickerCandidates, setPickerCandidates] = useState(null);

  // Org cache (keyed by env) — loaded lazily on first name-search Run.
  const [orgCache, setOrgCache] = useState({ prod: null, dev: null });
  const [orgCacheLoading, setOrgCacheLoading] = useState(false);

  useEffect(() => {
    onReady && onReady();
  }, [onReady]);

  const ensureOrgCache = useCallback(async () => {
    if (orgCache[env]) return orgCache[env];
    setOrgCacheLoading(true);
    const loadingToastId = toast.info("Loading orgs…", { autoClose: false });
    try {
      const db = env === "dev" ? firestoreDev : firestoreProd;
      const orgs = await loadAllOrgs(db);
      setOrgCache((c) => ({ ...c, [env]: orgs }));
      toast.update(loadingToastId, {
        render: `Loaded ${orgs.length} orgs`,
        type: "success",
        autoClose: 2000,
      });
      return orgs;
    } catch (err) {
      console.error(err);
      toast.update(loadingToastId, {
        render: "Failed to load organizations list",
        type: "error",
        autoClose: 3000,
      });
      return [];
    } finally {
      setOrgCacheLoading(false);
    }
  }, [env, orgCache]);

  const runForOrg = useCallback(
    async (orgId, displayName) => {
      const trimmed = orgId.trim();
      if (!trimmed) return;
      setLoading(true);
      setRows(null);
      setPickerCandidates(null);
      try {
        const db = env === "dev" ? firestoreDev : firestoreProd;
        const { rows } = await runReport(db, trimmed);
        setRows(rows);
        setSearchedOrgId(trimmed);
        setSearchedOrgName(displayName || "");
        if (!rows.length) toast.info("No members found for this org.");
        else toast.success(`Loaded ${rows.length} members.`);
      } catch (err) {
        console.error(err);
        toast.error(err.message || "Failed to load report");
      } finally {
        setLoading(false);
      }
    },
    [env]
  );

  const handleRun = useCallback(async () => {
    const id = orgIdInput.trim();
    const email = emailInput.trim();
    const name = nameInput.trim();

    setPickerCandidates(null);

    // 1. Direct org ID wins.
    if (id) {
      const cached = (orgCache[env] || []).find((o) => o.id === id);
      runForOrg(id, cached ? orgDisplayName(cached) : "");
      return;
    }

    // 2. Email path.
    if (email) {
      setLoading(true);
      try {
        const db = env === "dev" ? firestoreDev : firestoreProd;
        const ids = await findOrgIdsByEmail(db, email);
        if (ids.length === 0) {
          toast.error(`No orgs found with member email "${email}".`);
          return;
        }
        const cache = await ensureOrgCache();
        const candidates = ids.map((oid) => {
          const o = (cache || []).find((x) => x.id === oid);
          return o || { id: oid, name: "(org not in cache)" };
        });
        if (candidates.length === 1) {
          runForOrg(candidates[0].id, orgDisplayName(candidates[0]));
        } else {
          setPickerCandidates(candidates);
          toast.info(`${candidates.length} orgs match — pick one.`);
        }
      } catch (err) {
        console.error(err);
        toast.error(err.message || "Email lookup failed");
      } finally {
        setLoading(false);
      }
      return;
    }

    // 3. Name path (fuzzy).
    if (name) {
      setLoading(true);
      try {
        const cache = await ensureOrgCache();
        const matches = fuzzyMatchOrgs(cache || [], name);
        if (matches.length === 0) {
          toast.error(`No orgs matching "${name}".`);
          return;
        }
        if (matches.length === 1) {
          runForOrg(matches[0].id, orgDisplayName(matches[0]));
        } else {
          setPickerCandidates(matches);
          toast.info(`${matches.length} orgs match — pick one.`);
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    toast.error("Enter an org ID, email, or name.");
  }, [
    orgIdInput,
    emailInput,
    nameInput,
    env,
    orgCache,
    ensureOrgCache,
    runForOrg,
  ]);

  const handleFieldKeyDown = (e) => {
    if (e.key === "Enter") handleRun();
  };

  const scopedRows = useMemo(() => {
    if (!rows) return null;
    return excludeAddy ? rows.filter((r) => !isAddyTeam(r.email)) : rows;
  }, [rows, excludeAddy]);

  const addyTeamCount = useMemo(
    () => (rows ? rows.filter((r) => isAddyTeam(r.email)).length : 0),
    [rows]
  );

  const stats = useMemo(() => {
    if (!scopedRows) return null;
    const total = scopedRows.length;
    const pending = scopedRows.filter((r) => r.status === "pending").length;
    const accepted = scopedRows.filter((r) => r.status === "accepted").length;
    const hasUserDoc = scopedRows.filter((r) => r.hasUserDoc).length;
    const ext = scopedRows.filter((r) => r.extensionInstalled).length;
    const quest = scopedRows.filter((r) => r.hasQuestionnaire).length;
    const tour = scopedRows.filter((r) => r.finishedFirstTour).length;
    const anyOB = scopedRows.filter((r) => r.hasAnyFinishedOnboarding).length;
    return { total, pending, accepted, hasUserDoc, ext, quest, tour, anyOB };
  }, [scopedRows]);

  const filtered = useMemo(() => {
    if (!scopedRows) return [];
    if (filterStatus === "all") return scopedRows;
    return scopedRows.filter((r) => r.status === filterStatus);
  }, [scopedRows, filterStatus]);

  const pct = (n) =>
    stats && stats.total ? Math.round((n / stats.total) * 100) : 0;

  return (
    <div className="container-fluid OrgUsersReportPageContainer" style={S.page}>
      <ToastContainer position="bottom-right" theme="light" />

      <div style={S.header}>
        <h1 style={S.h1}>
          <span style={S.h1Icon}>
            <FaUsers />
          </span>
          Org Users Report
        </h1>
        <p style={S.subtitle}>
          Invite, onboarding, and extension-install breakdown for a single organization.
        </p>
      </div>

      <div style={S.tabsWrap}>
        <button
          type="button"
          style={S.mainTab(activeTab === "report")}
          onClick={() => setActiveTab("report")}
        >
          <FaUsers /> Org report
        </button>
        <button
          type="button"
          style={S.mainTab(activeTab === "top-clients")}
          onClick={() => setActiveTab("top-clients")}
        >
          <FaTrophy /> Top clients
        </button>
      </div>

      {activeTab === "top-clients" ? (
        <TopClientsTab onReady={onReady} />
      ) : null}

      {activeTab === "report" ? (
      <>
      {/* Controls */}
      <div style={S.controls}>
        <div style={S.fieldsRow}>
          <div style={S.fieldCol}>
            <div style={S.fieldLabel}>Environment</div>
            <select
              value={env}
              onChange={(e) => setEnv(e.target.value)}
              style={S.fieldInput}
            >
              <option value="prod">prod (hey-addy-chatgpt)</option>
              <option value="dev">dev (addy-ai-dev)</option>
            </select>
          </div>
          <div style={S.fieldCol}>
            <div style={S.fieldLabel}>Org name (fuzzy)</div>
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={handleFieldKeyDown}
              placeholder="e.g. emc"
              style={S.fieldInput}
            />
          </div>
          <div style={S.fieldCol}>
            <div style={S.fieldLabel}>Owner / member email</div>
            <input
              type="text"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleFieldKeyDown}
              placeholder="someone@company.com"
              style={S.fieldInput}
            />
          </div>
          <div style={S.fieldCol}>
            <div style={S.fieldLabel}>Org ID</div>
            <input
              type="text"
              value={orgIdInput}
              onChange={(e) => setOrgIdInput(e.target.value)}
              onKeyDown={handleFieldKeyDown}
              placeholder="aGWer6DsXt9xHRelW2kl"
              style={{ ...S.fieldInput, fontFamily: "monospace" }}
            />
          </div>
        </div>
        <div style={S.fieldHint}>
          Org ID wins over email, which wins over name. Nothing runs until you click <b>Run report</b>.
        </div>
        <div style={S.actionRow}>
          <button
            onClick={handleRun}
            disabled={loading}
            style={{
              ...S.btnPrimary,
              opacity: loading ? 0.65 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? <FaSync className="obp-spin" /> : <FaSearch />}
            {loading
              ? orgCacheLoading
                ? "Loading orgs…"
                : "Loading…"
              : "Run report"}
          </button>
          {rows && rows.length ? (
            <button
              onClick={() =>
                downloadCsv(
                  `org-${searchedOrgId}${
                    excludeAddy ? "-clients" : "-all"
                  }-report.csv`,
                  scopedRows || []
                )
              }
              style={S.btnGhost}
            >
              <FaDownload /> CSV
            </button>
          ) : null}
          {rows && rows.length ? (
            <label style={S.toggleWrap} title="Hides @addy-ai.com members">
              <input
                type="checkbox"
                checked={excludeAddy}
                onChange={(e) => setExcludeAddy(e.target.checked)}
                style={S.toggleInput}
              />
              <span>
                Exclude Addy team
                {addyTeamCount > 0 ? (
                  <span style={S.toggleCount}>({addyTeamCount})</span>
                ) : null}
              </span>
            </label>
          ) : null}
        </div>
      </div>

      {/* Picker card — shown when a name or email matches multiple orgs */}
      {pickerCandidates && pickerCandidates.length > 1 ? (
        <div style={S.pickerCard}>
          <div style={S.pickerLabel}>
            {pickerCandidates.length} matching orgs — pick one
          </div>
          {pickerCandidates.map((c) => (
            <div
              key={c.id}
              style={S.pickerRow}
              onClick={() => runForOrg(c.id, orgDisplayName(c))}
            >
              <div style={S.pickerRowName}>
                {orgDisplayName(c) || "(unnamed org)"}
              </div>
              <div style={S.pickerRowMeta}>{c.id}</div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Content */}
      {stats ? (
        <>
          {/* Top stats row — invites + extension */}
          <div style={S.statsRow}>
            <div style={S.statCard("#7f7fd5")}>
              <div style={S.statLabel}>Total members</div>
              <div style={S.statValue("#7f7fd5")}>{stats.total}</div>
              <div style={S.statSub}>
                <FaBuilding style={{ marginRight: 5, opacity: 0.6 }} />
                {searchedOrgName ? `${searchedOrgName} · ` : ""}
                {searchedOrgId}
              </div>
            </div>
            <div style={S.statCard("#f59e0b")}>
              <div style={S.statLabel}>Invited (pending)</div>
              <div style={S.statValue("#f59e0b")}>{stats.pending}</div>
              <div style={S.statSub}>
                <FaUserPlus style={{ marginRight: 5, opacity: 0.6 }} />
                invite sent, not joined
              </div>
            </div>
            <div style={S.statCard("#10b981")}>
              <div style={S.statLabel}>Joined (accepted)</div>
              <div style={S.statValue("#10b981")}>{stats.accepted}</div>
              <div style={S.statSub}>
                <FaUserCheck style={{ marginRight: 5, opacity: 0.6 }} />
                {pct(stats.accepted)}% of invited
              </div>
            </div>
            <div style={S.statCard("#3b82f6")}>
              <div style={S.statLabel}>Extension installed</div>
              <div style={S.statValue("#3b82f6")}>{stats.ext}</div>
              <div style={S.statSub}>
                <FaPuzzlePiece style={{ marginRight: 5, opacity: 0.6 }} />
                {pct(stats.ext)}% of members
              </div>
            </div>
          </div>

          {/* Onboarding stats row */}
          <div style={S.statsRow}>
            <div style={S.statCard("#8b5cf6")}>
              <div style={S.statLabel}>Has user doc</div>
              <div style={S.statValue("#8b5cf6")}>{stats.hasUserDoc}</div>
              <div style={S.statSub}>{pct(stats.hasUserDoc)}% of members</div>
            </div>
            <div style={S.statCard("#ec4899")}>
              <div style={S.statLabel}>Questionnaire filled</div>
              <div style={S.statValue("#ec4899")}>{stats.quest}</div>
              <div style={S.statSub}>
                <FaClipboardList style={{ marginRight: 5, opacity: 0.6 }} />
                {pct(stats.quest)}% completed signup form
              </div>
            </div>
            <div style={S.statCard("#06b6d4")}>
              <div style={S.statLabel}>Finished first-tour</div>
              <div style={S.statValue("#06b6d4")}>{stats.tour}</div>
              <div style={S.statSub}>
                <FaRoute style={{ marginRight: 5, opacity: 0.6 }} />
                {pct(stats.tour)}% of members
              </div>
            </div>
            <div style={S.statCard("#14b8a6")}>
              <div style={S.statLabel}>Any onboardings done</div>
              <div style={S.statValue("#14b8a6")}>{stats.anyOB}</div>
              <div style={S.statSub}>finishedOnboardings ≥ 1</div>
            </div>
          </div>

          {/* Funnel */}
          <div style={S.funnelWrap}>
            <div style={S.funnelLabel}>Adoption funnel</div>
            <div style={S.funnelRow}>
              {[
                { label: "invited", value: stats.total },
                { label: "accepted", value: stats.accepted },
                { label: "user doc", value: stats.hasUserDoc },
                { label: "questionnaire", value: stats.quest },
                { label: "first-tour", value: stats.tour },
                { label: "extension", value: stats.ext },
              ].map((s, i, arr) => (
                <React.Fragment key={s.label}>
                  <div style={S.funnelStep}>
                    <div style={S.funnelStepLabel}>{s.label}</div>
                    <div style={S.funnelStepValue}>{s.value}</div>
                  </div>
                  {i < arr.length - 1 ? (
                    <FaChevronRight style={S.funnelArrow} />
                  ) : null}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Table */}
          <div style={S.tableCard}>
            <div style={S.tableHeader}>
              <div style={S.tableTitle}>
                Members ({filtered.length}
                {filtered.length !== rows.length ? ` of ${rows.length}` : ""})
              </div>
              <div style={S.filterChips}>
                {["all", "accepted", "pending"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setFilterStatus(s)}
                    style={S.chip(filterStatus === s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead style={S.thead}>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th key={c.label} style={S.th}>
                        <span style={S.thInner}>
                          {c.label}
                          <InfoTip text={c.tip} />
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, idx) => (
                    <tr key={r.email + idx} style={S.tr}>
                      <td style={{ ...S.td, ...S.tdEmail }}>{r.email}</td>
                      <td style={S.td}>{r.role}</td>
                      <td style={S.td}>
                        <StatusPill status={r.status} />
                      </td>
                      <td style={S.td}>
                        <BoolPill value={r.hasUserDoc} />
                      </td>
                      <td style={S.td}>
                        <BoolPill value={r.hasQuestionnaire} />
                      </td>
                      <td style={S.td}>
                        <BoolPill value={r.finishedFirstTour} />
                      </td>
                      <td style={S.td}>
                        <BoolPill value={r.hasAnyFinishedOnboarding} />
                      </td>
                      <td style={S.td}>
                        <BoolPill value={r.extensionInstalled} />
                      </td>
                      <td style={{ ...S.td, ...S.tdMuted }}>
                        {r.dateSignedUp ? r.dateSignedUp.slice(0, 10) : "—"}
                      </td>
                      <td style={{ ...S.td, ...S.tdMuted }}>
                        {r.extensionInstalledAt
                          ? r.extensionInstalledAt.slice(0, 10)
                          : "—"}
                      </td>
                      <td
                        style={{ ...S.td, ...S.tdMutedWrap }}
                        title={r.finishedOnboardings}
                      >
                        {r.finishedOnboardings || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : loading ? (
        <div style={S.empty}>Loading org members...</div>
      ) : (
        <div style={S.empty}>
          <div style={{ fontSize: "2rem", marginBottom: 10, opacity: 0.4 }}>
            <FaUsers />
          </div>
          Fill in one of the fields above and click <b>Run report</b>.
        </div>
      )}
      </>
      ) : null}

      <style>{`
        .obp-spin { animation: obp-spin 1s linear infinite; }
        @keyframes obp-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default OrgUsersReportPage;
